import { ICoreOutput } from 'common/common.interface';

/**
 * @alias 유저의 작업결과 메일 수신여부 조회 output
 * @description 유저의 작업결과 메일 수신여부를 조회합니다.
 * @returns - 200 { success: true, result: boolean }
 * @returns - 400 { success: false, message: '존재하지 않는 유저입니다.' }
 */
export interface JobMailSubscriptionStatusOutput extends ICoreOutput {
  /**
   * @alias 작업결과 메일 수신여부
   */
  result: boolean;
}

/**
 * @alias 유저의 작업결과 메일 수신여부 토글 output
 * @description 유저의 작업결과 메일 수신여부를 토글합니다. (수신 <-> 수신거부)
 * @returns - 200 { success: true, jobMailYn: boolean }
 * @returns - 400 { success: false, message: '존재하지 않는 유저입니다.' }
 */
export interface JobMailSubscriptionToggleOutput extends ICoreOutput {
  /**
   * @alias 작업결과 메일 수신여부
   */
  jobMailYn: boolean;
}

/**
 * @alias 유저의 연동중인 스케줄러 상품리스트 조회 input
 */
export interface GetTrackingListInput {
  /**
   * @alias 검색 상태
   */
  searchStatus?: SearchStatusType;
  /**
   * @alias 검색 타입
   */
  searchType?: SearchType;
  /**
   * @alias 검색어
   */
  searchValue?: string;
  /**
   * @alias 페이지 번호
   */
  nowPage: number;
  /**
   * @alias 페이지 당 항목 수
   */
  viewCount: number;
}

/**
 * @alias 검색 상태 타입
 */
export enum SearchStatusType {
  /**
   * @alias 전체
   */
  ALL = 'ALL',
  /**
   * @alias 등록성공
   */
  REGIST_SUCCESS = 'REGIST_SUCCESS',
  /**
   * @alias 등록실패
   */
  REGIST_FAILURE = 'REGIST_FAILURE',
  /**
   * @alias 수정성공
   */
  UPDATE_SUCCESS = 'UPDATE_SUCCESS',
  /**
   * @alias 수정실패
   */
  UPDATE_FAILURE = 'UPDATE_FAILURE',
}

/**
 * @alias 검색 타입
 */
export enum SearchType {
  /**
   * @alias 수집 상품코드
   */
  SCRAP_PROD_CODE = 'scrapProdCode',
  /**
   * @alias 웨이크업 상품코드
   */
  WAKEUP_PROD_CODE = 'wakeupProdCode',
  /**
   * @alias 수집 상품명
   */
  SCRAP_PROD_NAME = 'scrapProdName',
}

/**
 * @alias 유저의 연동중인 스케줄러 상품리스트 조회 output
 */
export interface GetTrackingListOutput extends ICoreOutput {
  /**
   * @alias 전체 데이터 수
   */
  totalLength: number;
  /**
   * @alias 연동중인 스케줄러 상품리스트
   */
  result: TrackingProduct[];
}

/**
 * @alias 연동중인 스케줄러 상품
 */
export interface TrackingProduct {
  /**
   * @alias Tracking Prod List Table 고유값 숫자
   */
  listId: number;
  /**
   * @alias 생성일자
   * @example 2025-12-05T03:08:22.000Z
   */
  createdAt: string;
  /**
   * @alias 마지막 업데이트 일자
   * @example 2025-12-05T03:08:22.000Z
   */
  updatedAt: string;
  /**
   * @alias 마지막 상태
   */
  status: SearchStatusType;
  /**
   * @alias 마지막 작업 실패 사유
   * @example null
   */
  lastFailReason: string | null;
  /**
   * @alias 수집한 쇼핑몰 상품 코드
   * @example 12770463600
   */
  scrapProdCode: string;
  /**
   * @alias 가져오는 상품 정보 JSON
   */
  scrapProdInfo: ScrapProdInfo[];
  /**
   * @alias WakeUp 상품 등록 코드
   * @example 8911785225
   */
  wakeupProdCode: string;
  /**
   * @alias user Table 고유값 숫자
   * @example 258251
   */
  userId: string;
  /**
   * @alias scheduler Table 고유값 숫자
   * @example 22
   */
  schedulerId: number;
  /**
   * @alias scheduler Detail Table 고윳값 숫자
   * @example 20
   */
  schedulerDetailId: number;
  /**
   * @alias 스마트스토어 url
   */
  scrapShopEtc2: string;
}

/**
 * @alias 상품 이미지
 */
export interface ProductImage {
  /**
   * @alias 이미지 URL
   */
  url: string;
}

/**
 * @alias 상품 이미지 정보
 */
export interface ProductImages {
  /**
   * @alias 옵셔널 이미지
   */
  optionalImages: ProductImage[];
  /**
   * @alias 대표 이미지
   */
  representativeImage: ProductImage;
}

/**
 * @alias 리뷰 포인트 정책
 */
export interface ReviewPointPolicy {
  /**
   * @alias 텍스트 리뷰 포인트
   */
  textReviewPoint: number;
  /**
   * @alias 사진/동영상 리뷰 포인트
   */
  photoVideoReviewPoint: number;
  /**
   * @alias 스토어 멤버 리뷰 포인트
   */
  storeMemberReviewPoint: number;
  /**
   * @alias 사용 후 텍스트 리뷰 포인트
   */
  afterUseTextReviewPoint: number;
  /**
   * @alias 사용 후 사진/동영상 리뷰 포인트
   */
  afterUsePhotoVideoReviewPoint: number;
}

/**
 * @alias 할인 방법
 */
export interface DiscountMethod {
  /**
   * @alias 할인 값
   */
  value: number;
  /**
   * @alias 할인 단위
   */
  unitType: string;
}

/**
 * @alias 즉시 할인 정책
 */
export interface ImmediateDiscountPolicy {
  /**
   * @alias 할인 방법
   */
  discountMethod: DiscountMethod;
}

/**
 * @alias 고객 혜택
 */
export interface CustomerBenefit {
  /**
   * @alias 리뷰 포인트 정책
   */
  reviewPointPolicy: ReviewPointPolicy;
  /**
   * @alias 즉시 할인 정책
   */
  immediateDiscountPolicy: ImmediateDiscountPolicy;
}

/**
 * @alias 가져오는 상품 정보
 */
export interface ScrapProdInfo {
  /**
   * @alias 상품명
   */
  name: string;
  /**
   * @alias 상품 이미지
   */
  images: ProductImages;
  /**
   * @alias 판매가
   */
  salePrice: number;
  /**
   * @alias 고객 혜택
   */
  customerBenefit: CustomerBenefit;
}

/**
 * @alias 스케줄러 마이페이지 상품별 업데이트 input
 */
export interface RunSelectedProdInput {
  /**
   * @alias 스케줄러 마이페이지 상품별 업데이트 아이템 리스트
   */
  jobList: RunSelectedProdItem[];
}

/**
 * @alias 스케줄러 마이페이지 상품별 업데이트 아이템
 */
export interface RunSelectedProdItem {
  /**
   * @alias schedulerDetailId
   */
  schedulerDetailId: number;
  /**
   * @alias 웨이크업 상품 등록 코드
   */
  wakeupProdCode: string;
  /**
   * @alias 수집한 쇼핑몰 상품 코드
   */
  scrapProdCode: string;
}

/**
 * @alias 스케줄러 마이페이지 상품별 업데이트 output
 */
export interface RunSelectedProdOutput extends ICoreOutput {}

/**
 * @alias 스케줄러 마이페이지 일괄 업데이트 output
 */
export interface RunAllJobsOutput extends ICoreOutput {}

/**
 * @alias 유저의 상품별 업데이트 요청 카운트 조회 input
 */
export interface RequestCountSelectInput extends RunSelectedProdInput {}

/**
 * @alias 유저의 상품별 업데이트 요청 카운트 조회 output
 */
export interface RequestCountSelectOutput extends ICoreOutput {
  /**
   * @alias 유저의 상품별 업데이트 요청 카운트
   */
  resultList: RequestCountSelectItem[];
}

/**
 * @alias 유저의 상품별 업데이트 요청 카운트 조회 아이템
 */
export interface RequestCountSelectItem extends RunSelectedProdItem {
  /**
   * @alias 업데이트 요청 카운트
   */
  requestCount: number;
}

/**
 * @alias 유저의 스케줄러 일괄 업데이트 요청 카운트 조회 output
 */
export interface RequestCountAllOutput extends ICoreOutput {
  /**
   * @alias 업데이트 요청 카운트
   */
  requestCount: number;
}
